"use client";

import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import Tooltip from "@/app/components/lib/tooltip";
import { useTranslation } from "@/hooks/localization/useTranslations";

interface Props {
  quantity: number;
  setQuantity: (quantity: number) => void;
  /**
   * The most the shopper may order (stock on hand, capped by any per-cart maximum).
   * A typed value above it is never committed, so no request goes out — the backend
   * would reject it with a misleading "product unavailable". Omit for no limit.
   */
  max?: number;
}

// Long enough to read two short lines, short enough not to linger over the next action.
const NOT_CHANGED_NOTICE_MS = 4000;
// Saves a valid quantity once the shopper stops typing, for those who never tap away
// (common on mobile, where the keyboard stays up). Leaving the field still saves at once.
// Callers debounce another ~500ms, so the request leaves ~3.5s after the last keystroke.
const IDLE_COMMIT_MS = 3000;

const ExpandingQuantityInput: React.FC<Props> = ({
  quantity,
  setQuantity,
  max,
}) => {
  const t = useTranslation();
  const statusId = useId();
  const [value, setValue] = useState(quantity.toString());
  const [syncedQuantity, setSyncedQuantity] = useState(quantity);
  // Set when an over-stock value was rejected, so the shopper sees that nothing changed
  // rather than their number silently snapping back.
  const [showNotChanged, setShowNotChanged] = useState(false);
  // True between a keystroke and the next save/revert. Gates both the idle save and the
  // blur save, so a value already saved while idle is not sent again on blur.
  const [isEditing, setIsEditing] = useState(false);
  // Callers pass `setQuantity` inline, so it is a new function on every parent render.
  // As an effect dependency it restarted the idle timer on each of those renders, which
  // stretched the wait well past IDLE_COMMIT_MS; read through a ref, it cannot.
  const setQuantityRef = useRef(setQuantity);
  useLayoutEffect(() => {
    setQuantityRef.current = setQuantity;
  });

  // Follow the committed quantity (a +/- click, or the cart reverting a failed update).
  // Adjusted during render rather than in an effect, which would paint the stale value first.
  if (quantity !== syncedQuantity) {
    setSyncedQuantity(quantity);
    setValue(quantity.toString());
    setIsEditing(false);
  }

  useEffect(() => {
    if (!showNotChanged) return;
    const timer = setTimeout(() => setShowNotChanged(false), NOT_CHANGED_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [showNotChanged]);

  const numVal = parseFloat(value);
  const exceedsMax = max !== undefined && !isNaN(numVal) && numVal > max;
  const canCommit = !isNaN(numVal) && !exceedsMax && numVal !== quantity;

  // Only a valid value is saved while idle. An over-stock one stays as typed, with the
  // limit bubble showing, so the shopper can correct it; blurring reverts it as before.
  useEffect(() => {
    if (!isEditing || !canCommit) return;
    const timer = setTimeout(() => {
      setIsEditing(false);
      setQuantityRef.current(numVal);
    }, IDLE_COMMIT_MS);
    return () => clearTimeout(timer);
  }, [isEditing, canCommit, numVal]);

  const stockMessage =
    max !== undefined && max > 0
      ? t.quantity_exceeds_stock.replace("{max}", String(max))
      : t.quantity_not_available;
  // Shown live while typing, so the limit is clear BEFORE the shopper commits.
  const message = showNotChanged
    ? `${stockMessage}\n${t.quantity_not_changed}`
    : exceedsMax
      ? stockMessage
      : "";

  const commitValue = () => {
    setIsEditing(false);
    if (exceedsMax) {
      // Revert rather than clamp to `max`: silently putting the whole stock in the cart
      // of someone who mistyped 1000 for 100 is worse than asking them to pick again.
      setValue(quantity.toString());
      setShowNotChanged(true);
      return;
    }
    if (canCommit) setQuantity(numVal);
  };

  return (
    <Tooltip text={message} open={!!message} width="max-content">
      <input
        min={0}
        max={max}
        type="number"
        className="expanding-quantity-input"
        aria-invalid={exceedsMax || undefined}
        aria-describedby={statusId}
        style={{
          border: "none",
          padding: 0,
          width: "35px",
          background: "none",
          textAlign: "center",
        }}
        value={value}
        onChange={(e) => {
          setShowNotChanged(false);
          setIsEditing(true);
          setValue(e.target.value);
        }}
        onBlur={() => {
          if (value === "") {
            setIsEditing(false);
            setValue(quantity.toString());
          } else if (isEditing) {
            commitValue();
          }
        }}
        onKeyDown={(e) => {
          // Blurring commits (onBlur). Committing here as well sent the change twice.
          if (e.key === "Enter") e.currentTarget.blur();
        }}
      />
      {/* The tooltip is portalled to <body>; this keeps the same message announced to
          screen readers next to the input. */}
      <span id={statusId} className="visually-hidden" role="status">
        {message}
      </span>
    </Tooltip>
  );
};

export default ExpandingQuantityInput;
